// Pure, dependency-free logic for saved views: the load-time precedence
// resolution and an order-independent stringify used for autosave de-duping.
// Kept separate from savedFiltersApi (which pulls in aws-amplify/axios) so this
// can be unit-tested in isolation.

export type ResolveSource = 'preset' | 'url' | 'default' | 'last-used' | 'none'

/** Minimal structural shape of a saved filter row this logic needs. */
export interface SavedFilterLike {
  id: number
  name: string
  filters: Record<string, any>
  is_default: 0 | 1
}

export interface ResolveInput {
  /** id from the ?view= param, if any. */
  presetId?: number | null
  /** URL-derived filter values (the page's current initial-value). */
  urlValues?: Record<string, any> | null
  /** Whether the URL actually carries any active filter (vs empty defaults). */
  urlHasFilters?: boolean
  presets: SavedFilterLike[]
  lastUsed: SavedFilterLike | null
}

export interface ResolveResult {
  source: ResolveSource
  /** The filter values a page should apply, or null to use the page's own defaults. */
  values: Record<string, any> | null
  /** The preset id that was applied (for showing the current view name), or null. */
  appliedPresetId: number | null
}

/**
 * Precedence for what a page shows on load (highest first):
 *   1. ?view=<id> route param   — explicit "open this preset"
 *   2. URL filter params        — a deep link / bookmark the user is on right now
 *   3. the user's default preset — a stored "always start here" choice
 *   4. the auto-saved last-used  — implicit restore of what they had (OTC-1718)
 *   5. nothing                   — page falls back to its own defaults
 */
export function resolveInitialFilters(input: ResolveInput): ResolveResult {
  const { presetId, urlValues, urlHasFilters, presets, lastUsed } = input

  if (presetId != null) {
    const preset = presets.find((p) => p.id === presetId)
    if (preset) return { source: 'preset', values: preset.filters, appliedPresetId: preset.id }
  }

  if (urlHasFilters && urlValues) {
    return { source: 'url', values: urlValues, appliedPresetId: null }
  }

  const def = presets.find((p) => p.is_default === 1)
  if (def) return { source: 'default', values: def.filters, appliedPresetId: def.id }

  if (lastUsed) return { source: 'last-used', values: lastUsed.filters, appliedPresetId: null }

  return { source: 'none', values: null, appliedPresetId: null }
}

/** Order-independent stringify so re-saving the same filters (different key order) is a no-op. */
export function stableStringify(value: any): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return '[' + value.map(stableStringify).join(',') + ']'
  const keys = Object.keys(value).sort()
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + stableStringify(value[k])).join(',') + '}'
}

/**
 * Rows saved on a per-entity page (one board per project, one results page per
 * survey) carry the entity under `filters._entity`. A row belongs to the current
 * entity when its id matches; a row with no `_entity` (saved before scoping existed)
 * belongs to every entity so it keeps showing where it always did.
 */
export function rowMatchesEntity(filters: Record<string, any> | null | undefined, entityId: string | null): boolean {
  if (entityId == null) return true
  const stored = filters ? filters._entity : null
  if (!stored || stored.id == null || stored.id === '') return true
  return String(stored.id) === entityId
}
